import * as React from "react";
import { Button, Modal, Box, SpaceBetween, FormField, Multiselect } from "@amzn/awsui-components-react";
import { usePageEvaluation } from "../../hooks/usePageEvaluation";
import { ContentEvaluationModal } from "../content-evaluation/ContentEvaluationModal";
import { ContentEvaluationDisclaimer } from "../common/ContentEvaluationDisclaimer";
import { EntityType, EntityRef } from "@amzn/ask-legal-domain";

// Component interfaces
export interface PageContentEvaluationProps {
    pageRef?: EntityRef;
    draftRef?: EntityRef;
    pageId?: string;  // For loading configuration when using draftRef
    onEvaluationStart?: () => void;
}

export interface PageEvaluationConfiguration {
    evaluationGranularity: "Page" | "Container";
    containerIds: string[];
}

export const PageContentEvaluation = (props: PageContentEvaluationProps) => {
    const [showContainerSelection, setShowContainerSelection] = React.useState(false);
    const [showEvaluationModal, setShowEvaluationModal] = React.useState(false);
    const [selectedContainerIds, setSelectedContainerIds] = React.useState<string[]>([]);

    const {
        configuration,
        hasConfiguration,
        containerMetadata,
        containerRefs,
        isLoading,
        isDeprecated,
        entityRef: pageEntityRef,
        reloadConfiguration,
        pageName
    } = usePageEvaluation(props.pageRef, props.draftRef, props.pageId);

    const isPageLevelEvaluation = configuration?.evaluationGranularity === "Page";
    const availableContainerIds = configuration?.containerIds || containerRefs.map(ref => ref.id);

    // Initialize selected containers
    React.useEffect(() => {
        if (availableContainerIds.length > 0 && selectedContainerIds.length === 0) {
            setSelectedContainerIds([availableContainerIds[0]]);
        }
    }, [availableContainerIds, selectedContainerIds.length]);

    const handleEvaluateClick = () => {
        if (isPageLevelEvaluation) {
            setShowEvaluationModal(true);
        } else {
            setShowContainerSelection(true);
        }
        props.onEvaluationStart?.();
    };

    const handleContainerSelectionDismiss = () => {
        setShowContainerSelection(false);
    };

    const handleContinueToEvaluation = () => {
        setShowContainerSelection(false);
        setShowEvaluationModal(true);
    };

    const handleEvaluationModalDismiss = () => {
        setShowEvaluationModal(false);
    };

    const containerOptions = availableContainerIds.map(id => ({
        value: id,
        label: containerMetadata[id]?.title || `Container: ${id}`
    }));

    const selectedOptions = selectedContainerIds.map(id => ({
        value: id,
        label: containerMetadata[id]?.title || `Container: ${id}`
    }));

    // Only show the button if we have a valid configuration and it's not deprecated
    if (!hasConfiguration || isDeprecated) {
        return null;
    }

    return (
        <>
            <Button
                iconName="gen-ai"
                onClick={handleEvaluateClick}
                variant="primary"
                loading={isLoading}
            >
                Evaluate Content
            </Button>

            {/* Container Selection Modal */}
            <Modal
                visible={!isPageLevelEvaluation && showContainerSelection}
                onDismiss={handleContainerSelectionDismiss}
                header="Select Containers for Evaluation"
                size="medium"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="normal" onClick={handleContainerSelectionDismiss}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleContinueToEvaluation}
                                disabled={selectedContainerIds.length === 0}
                            >
                                Continue
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <ContentEvaluationDisclaimer />
                <FormField
                    label="Select containers to evaluate against"
                    description="Choose which containers' rules to use for content evaluation. You can select multiple containers to evaluate the same content against different rule sets."
                >
                    <Multiselect
                        loading={isLoading}
                        selectedOptions={selectedOptions}
                        onChange={({ detail }) =>
                            setSelectedContainerIds(detail.selectedOptions.map(option => option.value))
                        }
                        options={containerOptions}
                        placeholder="Select containers..."
                    />
                </FormField>
            </Modal>

            {/* Content Evaluation Modal */}
            <ContentEvaluationModal
                visible={showEvaluationModal}
                onDismiss={handleEvaluationModalDismiss}
                entityRef={pageEntityRef}
                pageId={props.pageId || props.pageRef?.id || props.draftRef?.id}
                selectedContainerIds={isPageLevelEvaluation ? undefined : selectedContainerIds}
                containerMetadata={containerMetadata}
                pageName={pageName}
                isPageLevelEvaluation={isPageLevelEvaluation}
            />
        </>
    );
};
